import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { StylePreference } from '@wtf/shared'
import { getMyPreferences } from '@wtf/shared/api/users'
import { useOnboardingStore } from './onboarding'

interface UserPreferencesState {
    stylePreference: StylePreference | null
    preferredCurrency: string | null
    isLoading: boolean
    setStylePreference: (preference: StylePreference | null) => void
    setPreferredCurrency: (currency: string | null) => void
    loadPreferences: () => Promise<void>
    clearPreferences: () => void
}

/**
 * Store for managing user preferences (stylePreference and preferredCurrency)
 * Persisted to localStorage for instant access on page load
 */
export const useUserPreferences = create<UserPreferencesState>()(
    persist(
        (set) => ({
            stylePreference: null,
            preferredCurrency: null,
            isLoading: false,

            setStylePreference: (preference) =>
                set({ stylePreference: preference }),

            setPreferredCurrency: (currency) =>
                set({ preferredCurrency: currency }),

            loadPreferences: async () => {
                set({ isLoading: true })
                try {
                    const preferences = await getMyPreferences()
                    set({
                        stylePreference: preferences.stylePreference,
                        preferredCurrency: preferences.preferredCurrency,
                        isLoading: false,
                    })
                } catch (error) {
                    const is404 =
                        (error as { statusCode?: number })?.statusCode === 404

                    // Only suppress 404 during active OAuth registration flow
                    // In other cases, 404 is a legitimate error (account deleted, etc.)
                    const { registrationData } = useOnboardingStore.getState()
                    const isPendingOAuthRegistration =
                        (registrationData.authMethod === 'google' ||
                            registrationData.authMethod === 'apple') &&
                        registrationData.registrationToken

                    if (!is404 || !isPendingOAuthRegistration) {
                        console.error('Failed to load user preferences:', error)
                    }
                    set({ isLoading: false })
                }
            },

            clearPreferences: () =>
                set({ stylePreference: null, preferredCurrency: null }),
        }),
        {
            name: 'user-preferences-storage',
        }
    )
)
